import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency, CDPercentage } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

// Adjustable Payment (AP) Table
export interface AdjustablePaymentData {
  indexPlusMargin: string;
  initialInterestRate: number;
  minimumMaximumInterestRate: string;
  changeFrequency: {
    firstChange: string;
    subsequentChanges: string;
  };
  limitsOnChanges: {
    firstChange: string;
    subsequentChanges: string;
  };
}

export interface CDAdjustablePaymentTableProps {
  /** AP table data */
  data: Partial<AdjustablePaymentData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<AdjustablePaymentData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDAdjustablePaymentTable - Adjustable Payment (AP) Table on Page 4
 *
 * Shows details about adjustable payment features (if applicable).
 */
export const CDAdjustablePaymentTable = React.forwardRef<
  HTMLDivElement,
  CDAdjustablePaymentTableProps
>(({ data, editable = false, onChange, className }, ref) => {
  // Helper to update top-level fields
  const updateField = (field: keyof AdjustablePaymentData, value: string | number) => {
    onChange?.({ ...data, [field]: value });
  };

  // Helper to update nested change frequency
  const updateChangeFrequency = (
    field: keyof AdjustablePaymentData['changeFrequency'],
    value: string,
  ) => {
    onChange?.({
      ...data,
      changeFrequency: {
        ...data.changeFrequency,
        [field]: value,
      } as AdjustablePaymentData['changeFrequency'],
    });
  };

  // Helper to update nested limits on changes
  const updateLimitsOnChanges = (
    field: keyof AdjustablePaymentData['limitsOnChanges'],
    value: string,
  ) => {
    onChange?.({
      ...data,
      limitsOnChanges: {
        ...data.limitsOnChanges,
        [field]: value,
      } as AdjustablePaymentData['limitsOnChanges'],
    });
  };

  return (
    <div ref={ref} className={cn('', className)}>
      <CDSectionHeader title="Adjustable Payment (AP) Table" />

      <div className="border border-t-0 border-cd-black">
        <table className="w-full border-collapse cd-text-body">
          <tbody>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold w-1/2">
                Index + Margin
              </td>
              <td className="border border-cd-black p-2">
                <CDEditableText
                  value={data.indexPlusMargin || ''}
                  editable={editable}
                  onChange={(v) => updateField('indexPlusMargin', v)}
                  size="small"
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Initial Interest Rate
              </td>
              <td className="border border-cd-black p-2">
                <CDPercentage
                  value={data.initialInterestRate}
                  editable={editable}
                  onChange={(v) => updateField('initialInterestRate', v)}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Minimum/Maximum Interest Rate
              </td>
              <td className="border border-cd-black p-2">
                <CDEditableText
                  value={data.minimumMaximumInterestRate || ''}
                  editable={editable}
                  onChange={(v) => updateField('minimumMaximumInterestRate', v)}
                  size="small"
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Change Frequency
              </td>
              <td className="border border-cd-black p-2">
                <div className="flex items-center gap-1">
                  First Change:{' '}
                  <CDEditableText
                    value={data.changeFrequency?.firstChange || ''}
                    editable={editable}
                    onChange={(v) => updateChangeFrequency('firstChange', v)}
                    size="small"
                  />
                </div>
                <div className="flex items-center gap-1">
                  Subsequent Changes:{' '}
                  <CDEditableText
                    value={data.changeFrequency?.subsequentChanges || ''}
                    editable={editable}
                    onChange={(v) => updateChangeFrequency('subsequentChanges', v)}
                    size="small"
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Limits on Interest Rate Changes
              </td>
              <td className="border border-cd-black p-2">
                <div className="flex items-center gap-1">
                  First Change:{' '}
                  <CDEditableText
                    value={data.limitsOnChanges?.firstChange || ''}
                    editable={editable}
                    onChange={(v) => updateLimitsOnChanges('firstChange', v)}
                    size="small"
                  />
                </div>
                <div className="flex items-center gap-1">
                  Subsequent Changes:{' '}
                  <CDEditableText
                    value={data.limitsOnChanges?.subsequentChanges || ''}
                    editable={editable}
                    onChange={(v) => updateLimitsOnChanges('subsequentChanges', v)}
                    size="small"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});

CDAdjustablePaymentTable.displayName = 'CDAdjustablePaymentTable';

// Adjustable Interest Rate (AIR) Table
export interface AdjustableInterestRateData {
  indexPlusMargin: string;
  initialInterestRate: number;
  minimumMaximumInterestRate: string;
  changeFrequency: {
    firstChange: string;
    subsequentChanges: string;
  };
  limitsOnChanges: {
    firstChange: string;
    subsequentChanges: string;
  };
  firstChangeDate: string;
  maximumPayment: number;
}

export interface CDAdjustableInterestRateTableProps {
  /** AIR table data */
  data: Partial<AdjustableInterestRateData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<AdjustableInterestRateData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDAdjustableInterestRateTable - Adjustable Interest Rate (AIR) Table on Page 4
 *
 * Shows details about adjustable interest rate features (if applicable).
 */
export const CDAdjustableInterestRateTable = React.forwardRef<
  HTMLDivElement,
  CDAdjustableInterestRateTableProps
>(({ data, editable = false, onChange, className }, ref) => {
  // Helper to update top-level fields
  const updateField = (field: keyof AdjustableInterestRateData, value: string | number) => {
    onChange?.({ ...data, [field]: value });
  };

  // Helper to update nested change frequency
  const updateChangeFrequency = (
    field: keyof AdjustableInterestRateData['changeFrequency'],
    value: string,
  ) => {
    onChange?.({
      ...data,
      changeFrequency: {
        ...data.changeFrequency,
        [field]: value,
      } as AdjustableInterestRateData['changeFrequency'],
    });
  };

  // Helper to update nested limits on changes
  const updateLimitsOnChanges = (
    field: keyof AdjustableInterestRateData['limitsOnChanges'],
    value: string,
  ) => {
    onChange?.({
      ...data,
      limitsOnChanges: {
        ...data.limitsOnChanges,
        [field]: value,
      } as AdjustableInterestRateData['limitsOnChanges'],
    });
  };

  return (
    <div ref={ref} className={cn('', className)}>
      <CDSectionHeader title="Adjustable Interest Rate (AIR) Table" />

      <div className="border border-t-0 border-cd-black">
        <table className="w-full border-collapse cd-text-body">
          <tbody>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold w-1/2">
                Index + Margin
              </td>
              <td className="border border-cd-black p-2">
                <CDEditableText
                  value={data.indexPlusMargin || ''}
                  editable={editable}
                  onChange={(v) => updateField('indexPlusMargin', v)}
                  size="small"
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Initial Interest Rate
              </td>
              <td className="border border-cd-black p-2">
                <CDPercentage
                  value={data.initialInterestRate}
                  editable={editable}
                  onChange={(v) => updateField('initialInterestRate', v)}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Minimum/Maximum Interest Rate
              </td>
              <td className="border border-cd-black p-2">
                <CDEditableText
                  value={data.minimumMaximumInterestRate || ''}
                  editable={editable}
                  onChange={(v) => updateField('minimumMaximumInterestRate', v)}
                  size="small"
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Change Frequency
              </td>
              <td className="border border-cd-black p-2">
                <div className="flex items-center gap-1">
                  First Change:{' '}
                  <CDEditableText
                    value={data.changeFrequency?.firstChange || ''}
                    editable={editable}
                    onChange={(v) => updateChangeFrequency('firstChange', v)}
                    size="small"
                  />
                </div>
                <div className="flex items-center gap-1">
                  Subsequent Changes:{' '}
                  <CDEditableText
                    value={data.changeFrequency?.subsequentChanges || ''}
                    editable={editable}
                    onChange={(v) => updateChangeFrequency('subsequentChanges', v)}
                    size="small"
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Limits on Interest Rate Changes
              </td>
              <td className="border border-cd-black p-2">
                <div className="flex items-center gap-1">
                  First Change:{' '}
                  <CDEditableText
                    value={data.limitsOnChanges?.firstChange || ''}
                    editable={editable}
                    onChange={(v) => updateLimitsOnChanges('firstChange', v)}
                    size="small"
                  />
                </div>
                <div className="flex items-center gap-1">
                  Subsequent Changes:{' '}
                  <CDEditableText
                    value={data.limitsOnChanges?.subsequentChanges || ''}
                    editable={editable}
                    onChange={(v) => updateLimitsOnChanges('subsequentChanges', v)}
                    size="small"
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                First Change/Payment Due Date After
              </td>
              <td className="border border-cd-black p-2">
                <CDEditableText
                  value={data.firstChangeDate || ''}
                  editable={editable}
                  onChange={(v) => updateField('firstChangeDate', v)}
                  size="small"
                />
              </td>
            </tr>
            <tr>
              <td className="border border-cd-black p-2 bg-cd-gray-header font-bold">
                Maximum Payment
              </td>
              <td className="border border-cd-black p-2">
                <CDCurrency
                  value={data.maximumPayment}
                  editable={editable}
                  onChange={(v) => updateField('maximumPayment', v)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});

CDAdjustableInterestRateTable.displayName = 'CDAdjustableInterestRateTable';
